
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import api from '../services/api';

const BlogPage = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [blogTopics, setBlogTopics] = useState([]);
  const limit = 10; // Number of posts per page

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.getPublic('blog-categories');
        if (response && Array.isArray(response)) {
          setBlogTopics(["All", ...response.map(c => c.name)]);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);

    // const fetchTrendingPosts = async () => {
    //   try {
    //     const response = await api.getPublic('blogs?type=trending&limit=4');
    //     if (response && Array.isArray(response.rows)) {
    //       setFeaturedPost(response.rows[0]);
    //       setTrendingPosts(response.rows.slice(1));
    //     }
    //   } catch (error) {
    //     console.error('Error fetching trending posts:', error);
    //   }
    // };

  useEffect(() => {
    const fetchTrendingPosts = async () => {
      try {
        const response = await api.getPublic('blogs?type=trending&limit=4');
        if (response && Array.isArray(response.rows)) {
          setFeaturedPost(response.rows[0]);
          setTrendingPosts(response.rows.slice(1));
        }
      } catch (error) {
        console.error('Error fetching trending posts:', error);
      }
    };

    fetchTrendingPosts();
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      setError(null);
      try {
        let url = `blogs?page=${currentPage}&limit=${limit}`;
        if (selectedTag !== 'All') {
          url += `&category=${selectedTag}`;
        }
        // Add sorting parameters if needed, e.g., &sortBy=createdAt&order=desc

        const response = await api.getPublic(url);
        if (response && Array.isArray(response.rows)) {
          setBlogPosts(response.rows);
          setTotalPages(Math.ceil(response.count / limit));
        } else {
          setError('API did not return an array of blogs or invalid response structure.');
        }
      } catch (error) {
        setError(error.message);
      }
      setLoading(false);
    };

    fetchBlogs();
  }, [selectedTag, currentPage]);



  const handleReadMore = (id) => {
    navigate(`/blog/${id}`);
  };

  const handleTagClick = (tag) => {
    setSelectedTag(tag);
    setCurrentPage(1); // Reset to first page when tag changes
  };

  if (loading) {
    return <div className="text-center py-20">Loading...</div>;
  }

  if (error) {
    return <div className="text-center py-20">Error: {error}</div>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#264864] text-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 px-6 text-center">
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(90deg, #264864 0%, #35a89d 100%)",
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto">
          <h1 className="text-5xl font-extrabold mb-6 mt-4">Blogs</h1>
          <p className="text-lg leading-relaxed opacity-90">
            Explore how we’ve partnered with clients to deliver impactful
            digital products — from AI-driven platforms to mobile and web
            applications.
          </p>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-6 px-6 bg-[#264864] text-center">
        <div className="flex justify-center gap-4 flex-wrap">
          {blogTopics.map((topic, index) => (
            <button
              key={index}
              onClick={() => handleTagClick(topic)}
              className={`px-5 py-2 rounded-md font-semibold transition ${ 
                selectedTag === topic
                  ? "bg-[#35a89d] text-white"
                  : "bg-white/10 hover:bg-white/20"
              }`}
            >
              {topic}
            </button>
          ))}
        </div>
      </section>

      {/* Blog Sections */}
      <section className="py-10 px-6 bg-[#264864]">
        <div className="container mx-auto">
          {/* Featured and Trending Section */}
          {(featuredPost || trendingPosts.length > 0) && (
            <div className="lg:flex lg:gap-10 mb-24 h-[600px]">
              {/* Featured Post */}
              {featuredPost && (
                <div className="lg:w-2/3 mb-10 lg:mb-0">
                  <h2 className="text-4xl font-bold mb-8 text-center lg:text-left">Featured Post</h2>
                  <div onClick={() => handleReadMore(featuredPost.id)} className="cursor-pointer relative group rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1 w-full h-full">
                    <div
                      className="h-full bg-cover bg-center relative"
                      style={{ backgroundImage: `url(${process.env.REACT_APP_API_BASE_URL}/${featuredPost.imageUrl})` }}
                    >
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition" />
                      <div className="absolute inset-0 flex items-end p-6">
                        <div>
                          <h3 className="text-4xl font-bold">{featuredPost.title}</h3>
                          <p className="text-lg opacity-80 mt-2">{featuredPost.author}</p>
                          <span className="text-blue-400 hover:underline cursor-pointer mt-4 inline-block">Read More &rarr;</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Trending Posts */}
              {trendingPosts.length > 0 && (
                <div className="lg:w-1/3">
                  <h2 className="text-4xl font-bold mb-8 text-center lg:text-left">Trending</h2>
                  <div className="flex flex-col gap-8 h-full">
                    {trendingPosts.map((post, index) => (
                      <div key={index} onClick={() => handleReadMore(post.id)} className="cursor-pointer relative group rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1 h-full">
                        <div
                          className="h-full bg-cover bg-center relative"
                          style={{ backgroundImage: `url(${process.env.REACT_APP_API_BASE_URL}/${post.imageUrl})` }}
                        >
                          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition" />
                          <div className="absolute bottom-0 w-full px-4 py-3 bg-[#35a89d]">
                            <h3 className="text-lg font-bold">{post.title}</h3>
                            <p className="text-sm opacity-80">{post.author}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Latest Posts */}
          <div>
            <h2 className="text-4xl font-bold mb-4 text-left">Latest Posts</h2>
            <hr className="border-white/20 mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
              {blogPosts.map((post, index) => (
                <div key={index} onClick={() => handleReadMore(post.id)} className="cursor-pointer relative group rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1">
                  <div
                    className="h-64 bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${process.env.REACT_APP_API_BASE_URL}/${post.imageUrl})` }}
                  >
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <span className="px-5 py-2 rounded-md font-semibold bg-white/90 text-[#264864] hover:bg-white hover:shadow-lg transition">Read More</span>
                    </div>
                    <div className="absolute bottom-0 w-full px-4 py-3 bg-[#35a89d]">
                      <h3 className="text-lg font-bold">{post.title}</h3>
                      <p className="text-sm opacity-80">{post.readTime}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-12 space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPage(index + 1)}
                  className={`px-4 py-2 rounded-md ${currentPage === index + 1 ? 'bg-[#35a89d]' : 'bg-white/10 hover:bg-white/20'}`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;